import { apiPost } from "../../services/genesisApi/httpsMethods";
import type { Contact, PartialDeal } from "../../types/deal";
import {
  normalizeAssetData,
  normalizeDealData,
  validateDealForCreation,
  type ValidationError,
} from "../../utils/dealValidation";
import { createNda } from "../../services/ndasApi";
import type { ExtractionNdaDraft } from "../../utils/extractionNda";

type ApiFail = {
  ok: false;
  validationErrors?: ValidationError[];
  apiResponse?: any;
  message?: string;
  title?: string;
  /**
   * Set when the deal itself was saved but one or more follow-up steps
   * (assets, NDA, contacts) failed. The caller must not resubmit the same
   * form, or the deal / assets would be created twice.
   */
  savedDealId?: number;
};

type ApiOk = {
  ok: true;
  dealId: number;
};

export type SaveCreateDealResult = ApiOk | ApiFail;

type SaveCreateDealArgs = {
  deal: PartialDeal;
  assets: any[];
  tenants: any[];
  contacts: any[];
  referenceContacts: Contact[];
  createdBy?: string | null;
  extractionId?: number | null;
  acceptExtractionNda?: boolean;
  extractionNdaDraft?: ExtractionNdaDraft | null;
};

function normalizeText(value: unknown) {
  return typeof value === "string" ? value.trim() : "";
}

function normalizeNullable(value: unknown) {
  const text = normalizeText(value);
  return text === "" ? null : text;
}

/** Short reason for a failed API step, for the partial-save summary. */
export function describeFailedResponse(response: {
  status?: number;
  message?: unknown;
}): string {
  const raw = response?.message;
  const text =
    typeof raw === "string"
      ? raw
      : raw && typeof raw === "object" && "message" in raw
        ? String((raw as { message?: unknown }).message || "")
        : "";
  if (text.trim()) return text.trim();
  return response?.status ? `status ${response.status}` : "request failed";
}

/**
 * Result for a save where the deal was written but later steps failed.
 * The message lists each failed step so the user knows what to redo.
 */
export function partialSaveFailure(
  dealId: number,
  action: "created" | "updated",
  failedSteps: string[],
): ApiFail {
  return {
    ok: false,
    savedDealId: dealId,
    title: "Deal Partially Saved",
    message: `The deal was ${action}, but these steps failed: ${failedSteps.join("; ")}. Open the deal to review and redo them.`,
  };
}

/** Resolve source_id from source_entity (select existing or create new). */
async function resolveSourceId(
  dealWithoutExtras: Record<string, any>,
): Promise<{ sourceId: number | null; cleared: boolean; error?: any }> {
  const sourceEntity = dealWithoutExtras.source_entity;

  const cleared =
    sourceEntity == null ||
    (typeof sourceEntity === "string" && sourceEntity.trim() === "") ||
    (typeof sourceEntity === "object" &&
      sourceEntity !== null &&
      typeof sourceEntity.id !== "number" &&
      typeof sourceEntity.source_name === "string" &&
      String(sourceEntity.source_name).trim() === "");

  const sourceNameToCreate =
    typeof sourceEntity === "string"
      ? sourceEntity.trim()
      : sourceEntity &&
          typeof sourceEntity === "object" &&
          typeof sourceEntity.source_name === "string"
        ? String(sourceEntity.source_name).trim()
        : null;

  const sourceIdFromEntity =
    sourceEntity &&
    typeof sourceEntity === "object" &&
    typeof sourceEntity.id === "number"
      ? sourceEntity.id
      : null;

  const existingSourceId =
    sourceIdFromEntity ??
    (cleared || sourceNameToCreate
      ? null
      : typeof dealWithoutExtras.source_id === "number"
        ? dealWithoutExtras.source_id
        : null);

  if (sourceNameToCreate) {
    const sourceResponse = await apiPost("sources", {
      source_name: sourceNameToCreate,
    });
    if (!sourceResponse.success) {
      return { sourceId: null, cleared, error: sourceResponse };
    }

    const createdSourceIdRaw =
      (sourceResponse.data as any)?.id ??
      (sourceResponse.data as any)?.source_id ??
      (sourceResponse.data as any)?.source_ids?.[0] ??
      (Array.isArray(sourceResponse.data)
        ? (sourceResponse.data as any)?.[0]?.id
        : null);

    const createdSourceId =
      typeof createdSourceIdRaw === "number"
        ? createdSourceIdRaw
        : typeof createdSourceIdRaw === "string" &&
            createdSourceIdRaw.trim() !== ""
          ? Number(createdSourceIdRaw)
          : null;

    return {
      sourceId: existingSourceId ?? createdSourceId,
      cleared,
    };
  }

  return { sourceId: existingSourceId, cleared };
}

/** Build tenants nested under one asset for POST /assets. */
function buildTenantsForAsset(asset: any, tenants: any[]) {
  const assetId = asset?.id;
  const assetName =
    typeof asset?.asset_name === "string"
      ? asset.asset_name.toLowerCase().trim()
      : "";

  const matched = tenants.filter((tenant: any) => {
    if (tenant?.shouldCreate === false || tenant?.included === false) {
      return false;
    }

    const tenantAssetId = tenant.asset_id ?? tenant.asset?.id;
    if (tenantAssetId != null && assetId != null) {
      return tenantAssetId.toString() === assetId.toString();
    }

    if (assetName) {
      const tenantAssetName =
        tenant.asset?.asset_name || tenant.asset_name || "";
      return (
        typeof tenantAssetName === "string" &&
        tenantAssetName.toLowerCase().trim() === assetName
      );
    }

    return false;
  });

  return matched.map((tenant: any) => {
    const next: any = { ...tenant };

    if (typeof next.id === "string") {
      delete next.id;
    }
    if ("asset" in next) delete next.asset;
    if ("asset_name" in next) delete next.asset_name;
    if ("shouldCreate" in next) delete next.shouldCreate;
    if ("included" in next) delete next.included;

    if (typeof assetId === "number" && assetId > 0) {
      next.asset_id = assetId;
    } else {
      delete next.asset_id;
    }

    const dateFields = [
      "lease_start_date",
      "lease_end_date",
      "break_option_date",
      "next_review_date",
      "rent_free_end_date",
    ];
    dateFields.forEach((field) => {
      const value = next[field];
      if (!value || (typeof value === "string" && value.trim() === "")) {
        next[field] = null;
      }
    });

    return next;
  });
}

type PrepareDealPayloadArgs = {
  deal: PartialDeal;
  createdBy?: string | null;
  updatedBy?: string | null;
  extractionId?: number | null;
};

/**
 * Validate + normalize the deal, create free-typed sponsor/source/borrowers,
 * and return the API payload used by both create and update.
 */
export async function prepareDealPayload({
  deal,
  createdBy,
  updatedBy,
  extractionId,
}: PrepareDealPayloadArgs): Promise<
  { ok: true; payload: any } | ApiFail
> {
  const normalizedDeal = normalizeDealData(deal);
  const validationErrors = validateDealForCreation(normalizedDeal);
  if (validationErrors.length > 0) {
    return {
      ok: false,
      validationErrors,
      message: `Please fix the following errors:\n${validationErrors
        .map((e) => `• ${e.message}`)
        .join("\n")}`,
      title: "Validation Error",
    };
  }

  // --- Sponsors ---
  let sponsorData: any = normalizedDeal.sponsor;
  if (typeof normalizedDeal.sponsor === "string" && normalizedDeal.sponsor) {
    const sponsorResponse = await apiPost("sponsors", {
      sponsor_name: normalizedDeal.sponsor,
    });
    if (!sponsorResponse.success) {
      return { ok: false, apiResponse: sponsorResponse };
    }
    sponsorData = sponsorResponse.data;
  }

  let secondarySponsorData: any = normalizedDeal.secondary_sponsor;
  if (
    typeof normalizedDeal.secondary_sponsor === "string" &&
    normalizedDeal.secondary_sponsor
  ) {
    const secondarySponsorResponse = await apiPost("sponsors", {
      sponsor_name: normalizedDeal.secondary_sponsor,
    });
    if (!secondarySponsorResponse.success) {
      return { ok: false, apiResponse: secondarySponsorResponse };
    }
    secondarySponsorData = secondarySponsorResponse.data;
  }

  // --- Borrowers ---
  let borrowerIds: number[] = [];
  if (
    Array.isArray(normalizedDeal.borrowers) &&
    normalizedDeal.borrowers.length > 0 &&
    sponsorData?.id
  ) {
    const borrowersResponse = await apiPost(
      `sponsor/${sponsorData.id}/borrowers`,
      { data: normalizedDeal.borrowers },
    );
    if (!borrowersResponse.success) {
      return { ok: false, apiResponse: borrowersResponse };
    }
    borrowerIds = (borrowersResponse.data || []).map(
      (borrower: any) => borrower.id,
    );
  }

  let secondaryBorrowerIds: number[] = [];
  if (
    Array.isArray(normalizedDeal.secondary_borrowers) &&
    normalizedDeal.secondary_borrowers.length > 0 &&
    secondarySponsorData?.id
  ) {
    const secondaryBorrowersResponse = await apiPost(
      `sponsor/${secondarySponsorData.id}/borrowers`,
      { data: normalizedDeal.secondary_borrowers },
    );
    if (!secondaryBorrowersResponse.success) {
      return { ok: false, apiResponse: secondaryBorrowersResponse };
    }
    secondaryBorrowerIds = (secondaryBorrowersResponse.data || []).map(
      (borrower: any) => borrower.id,
    );
  }

  // Strip UI-only fields
  const {
    tenant_column_ranges: _ranges,
    contacts: _dealContacts,
    ...dealWithoutExtras
  } = normalizedDeal as any;

  const sourceResult = await resolveSourceId(dealWithoutExtras);
  if (sourceResult.error) {
    return { ok: false, apiResponse: sourceResult.error };
  }

  const dealWithSource: any = {
    ...dealWithoutExtras,
    ...(sourceResult.cleared
      ? { source_id: null }
      : sourceResult.sourceId
        ? { source_id: sourceResult.sourceId }
        : {}),
  };
  delete dealWithSource.source_entity;
  delete dealWithSource.source;

  // Only keep persisted NDA ids on the deal payload
  const nda = dealWithSource.nda;
  if (nda && typeof nda === "object" && typeof nda.id === "number") {
    dealWithSource.nda = { id: nda.id };
    dealWithSource.nda_id = nda.id;
  } else if (typeof dealWithSource.nda_id === "number") {
    dealWithSource.nda = { id: dealWithSource.nda_id };
  } else {
    delete dealWithSource.nda;
    delete dealWithSource.nda_id;
  }

  const payload: any = {
    ...dealWithSource,
    sponsor: sponsorData,
    secondary_sponsor: secondarySponsorData,
    linked_borrower_ids: [...borrowerIds, ...secondaryBorrowerIds],
    created_by: createdBy || undefined,
    updated_by: updatedBy || undefined,
    ...(extractionId ? { extraction_id: extractionId } : {}),
  };
  delete payload.borrowers;
  delete payload.secondary_borrowers;

  return { ok: true, payload };
}

/**
 * Create-deal save flow:
 * 1) validate + normalize deal
 * 2) create free-typed sponsor / source if needed
 * 3) POST deal
 * 4) POST assets (+ nested tenants)
 * 5) create NDA from extraction draft if accepted
 * 6) create/link contacts
 */
export async function saveCreateDeal({
  deal,
  assets,
  tenants,
  contacts,
  referenceContacts,
  createdBy,
  extractionId,
  acceptExtractionNda,
  extractionNdaDraft,
}: SaveCreateDealArgs): Promise<SaveCreateDealResult> {
  const prepared = await prepareDealPayload({
    deal,
    createdBy,
    extractionId,
  });
  if (!prepared.ok) return prepared;

  const { payload } = prepared;

  // Validate the NDA draft before anything is written
  if (
    acceptExtractionNda &&
    extractionNdaDraft &&
    !extractionNdaDraft.expiry_date
  ) {
    return {
      ok: false,
      message:
        "NDA expiry date is required when creating a restriction from extraction.",
      title: "NDA Error",
    };
  }

  // --- Create deal ---
  const dealResponse = await apiPost("deals", payload);
  if (!dealResponse.success) {
    return { ok: false, apiResponse: dealResponse };
  }

  const dealId = dealResponse.data?.deal_ids?.[0];
  if (!dealId) {
    return {
      ok: false,
      message: "Deal was created but no id was returned.",
      title: "Create Deal Error",
    };
  }

  // From here on the deal exists: keep going and report every failed step
  // instead of stopping, so the user is never asked to resubmit the form.
  const failedSteps: string[] = [];

  // --- Create assets (+ tenants) ---
  // Every asset still in the table is created (exclude by deleting the row).
  for (const asset of assets) {
    const normalizedAsset = normalizeAssetData(asset);
    const assetPayload = {
      ...normalizedAsset,
      deal_id: dealId,
      tenants: buildTenantsForAsset(asset, tenants),
    };

    const assetResponse = await apiPost("assets", assetPayload);
    if (!assetResponse.success) {
      failedSteps.push(
        `create asset "${normalizeText(asset?.asset_name) || "Unnamed"}" (${describeFailedResponse(assetResponse)})`,
      );
    }
  }

  // --- Create NDA from extraction draft ---
  if (acceptExtractionNda && extractionNdaDraft) {
    const ndaResponse = await createNda({
      restriction_type: extractionNdaDraft.restriction_type,
      expiry_date: extractionNdaDraft.expiry_date,
      applies_to: extractionNdaDraft.applies_to,
      summary: extractionNdaDraft.summary,
      person_responsible_id: extractionNdaDraft.person_responsible_id,
      deal_id: dealId,
    });
    if (!ndaResponse.success) {
      failedSteps.push(`create NDA (${describeFailedResponse(ndaResponse)})`);
    }
  }

  // --- Create / link contacts ---
  for (const row of contacts) {
    if (row?.shouldCreate === false) continue;
    const contactLabel = normalizeText(row?.contact_name) || "Unnamed";

    let contactId: number | null =
      typeof row?.id === "number" ? row.id : null;

    if (!contactId) {
      const email = normalizeText(row?.contact_email).toLowerCase();
      if (email) {
        const existing = (referenceContacts || []).find(
          (c: any) =>
            typeof c?.contact_email === "string" &&
            c.contact_email.trim().toLowerCase() === email,
        );
        if (existing?.id) contactId = existing.id;
      }
    }

    if (!contactId) {
      const created = await apiPost("contacts", {
        contact_name: normalizeText(row?.contact_name),
        company: normalizeNullable(row?.company),
        contact_email: normalizeNullable(row?.contact_email),
        contact_phone: normalizeNullable(row?.contact_phone),
        contact_title: normalizeNullable(row?.contact_title),
        contact_location: normalizeNullable(row?.contact_location),
        notes: normalizeNullable(row?.notes),
      });
      if (!created.success) {
        failedSteps.push(
          `create contact "${contactLabel}" (${describeFailedResponse(created)})`,
        );
        continue;
      }
      const createdId = (created.data as any)?.id;
      if (typeof createdId !== "number") {
        failedSteps.push(
          `link contact "${contactLabel}" (contact was created but no id was returned)`,
        );
        continue;
      }
      contactId = createdId;
    }

    const linkResponse = await apiPost("contact-link", {
      contact_id: contactId,
      deal_id: dealId,
      sponsor_id: null,
      entity_id: null,
      source_id: null,
      role: null,
    });
    if (!linkResponse.success) {
      failedSteps.push(
        `link contact "${contactLabel}" (${describeFailedResponse(linkResponse)})`,
      );
    }
  }

  if (failedSteps.length > 0) {
    return partialSaveFailure(dealId, "created", failedSteps);
  }

  return { ok: true, dealId };
}
